import type { RoomLayoutInstanceHandle } from "building-types/handles/docEntityHandleTypes";
import type { RoomLayoutEntityType } from "building-types/roomLayouts/roomLayoutTypes";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import type { RoomLayoutRelatedHandle } from "../../rooms/layout/roomLayoutTools";
import { roomLayoutOrInstanceHandle } from "../../rooms/layout/roomLayoutTools";
import { docEntityHandleHash } from "../core/handleHash";
import { assertIsDocEntityOfType } from "../docEntityBaseTools";
import { isDocEntityNodeOfType } from "../docEntityBaseTools";
import type { DocEntityNode } from "../docEntityNode";
import { DocEntityNodeBase } from "../docEntityNodeBase";

type RoomLayoutOrInstanceDocEntityNode = DocEntityNode<"ROOM_LAYOUT"> | DocEntityNode<"ROOM_LAYOUT_INSTANCE">;

export class RoomLayoutEntityDocEntityNodeBase<T extends RoomLayoutEntityType> extends DocEntityNodeBase<T> {
  private roomLayoutOrInstanceCached: { [key: string]: RoomLayoutOrInstanceDocEntityNode | undefined } = {};
  getRoomLayoutInstance(contextHandle: RoomLayoutRelatedHandle): RoomLayoutOrInstanceDocEntityNode | undefined {
    const cacheKey = docEntityHandleHash(contextHandle);
    if (this.roomLayoutOrInstanceCached[cacheKey]) {
      return this.roomLayoutOrInstanceCached[cacheKey];
    }

    const roomLayoutEntity = this.getDocEntity();

    const hash = this.getHandleHash();

    switch (contextHandle.type) {
      case "ROOM_LAYOUT":
        return undefined;
      case "EDGE":
      case "ROOM": {
        const roomId = contextHandle.type === "EDGE" ? contextHandle.parent.id : contextHandle.id;

        const layoutOrInstanceHandle = roomLayoutOrInstanceHandle({
          docRoomLayoutInstances: this.doc.roomLayoutInstances,
          roomId,
          entityRoomLayouts: this.doc.entityRoomLayouts,
          entityHash: hash,
          entityId: roomLayoutEntity.id,
          docRoomLayouts: this.doc.roomLayouts,
        });

        if (!layoutOrInstanceHandle) {
          return undefined;
        }

        const roomLayoutOrInstanceNode = this.docEntityNodeSource.getNode(this.doc, layoutOrInstanceHandle);
        assertIsNotUndefined(roomLayoutOrInstanceNode);
        return (this.roomLayoutOrInstanceCached[cacheKey] =
          roomLayoutOrInstanceNode as RoomLayoutOrInstanceDocEntityNode);
      }
      case "ROOM_LAYOUT_INSTANCE": {
        const roomLayoutInstance = this.doc.roomLayoutInstances[contextHandle.id];

        if (!roomLayoutInstance) {
          return undefined;
        }

        const roomLayout = this.doc.roomLayouts[roomLayoutInstance.roomLayoutId];

        if (!roomLayout) {
          return undefined;
        }

        if (roomLayout.parentId !== roomLayoutEntity.parentId) {
          return undefined;
        }

        const roomLayoutInstanceHandle: RoomLayoutInstanceHandle = {
          type: "ROOM_LAYOUT_INSTANCE",
          id: contextHandle.id,
        };

        const roomLayoutInstanceNode = this.docEntityNodeSource.getNode(this.doc, roomLayoutInstanceHandle);
        assertIsNotUndefined(roomLayoutInstanceNode);

        return (this.roomLayoutOrInstanceCached[cacheKey] =
          roomLayoutInstanceNode as RoomLayoutOrInstanceDocEntityNode);
      }
    }
  }

  getSuppressedInRoomLayout(contextHandle: RoomLayoutRelatedHandle) {
    const roomLayoutInstanceNode = this.getRoomLayoutInstance(contextHandle);

    if (!roomLayoutInstanceNode) {
      return false;
    }

    const id = this.getId();
    assertIsNotUndefined(id);
    const roomLayoutInstance = roomLayoutInstanceNode.getDocEntity();

    if (!roomLayoutInstance || !isDocEntityNodeOfType(roomLayoutInstanceNode, "ROOM_LAYOUT_INSTANCE")) {
      return false;
    }

    assertIsDocEntityOfType(roomLayoutInstance, "ROOM_LAYOUT_INSTANCE");
    return roomLayoutInstance.suppressedEntities?.[id];
  }
}

export function isRoomLayoutEntityDocEntityNode<T extends RoomLayoutEntityType>(
  node: DocEntityNode,
): node is RoomLayoutEntityDocEntityNodeBase<T> {
  return node instanceof RoomLayoutEntityDocEntityNodeBase;
}

export function assertIsRoomLayoutEntityDocEntityNode<T extends RoomLayoutEntityType>(
  node: DocEntityNode | undefined,
): asserts node is RoomLayoutEntityDocEntityNodeBase<T> {
  assertIsNotUndefined(node);
  if (!isRoomLayoutEntityDocEntityNode<T>(node)) {
    throw new Error("Node is not a RoomLayoutEntityDocEntityNode");
  }
}
